import anime from 'animejs';
import Scene from '../classes/scene';
import SceneManager from '../classes/scene-manager';
import {
	clearElCache,
	getEl,
	getEls,
	isGreaterThanOrEqualTo,
	isSmallerThanOrEqualTo,
	setDownloadReportURLPage
} from "../helpers";
import ResponsiveWatcher, {ResponsiveSize} from "../classes/responsive-watcher";
import {ScrollDirection} from "../classes/scroll-manager";

class RadialGraph {

	el: Element;
	shadowEl: Element;
	rings: {value:number, class:string, path:SVGPathElement, shadow: SVGPathElement}[] = [];

	width: number = 600;
	height: number = this.width*1.33;
	hw: number;
	hh: number;

	thicknessScale: number = 1;

	constructor(svgEl: Element, svgShadowEl: Element, values: number[], classes: string[], thicknessScale: number = 1) {

		this.el = svgEl;
		this.shadowEl = svgShadowEl;
		this.thicknessScale = thicknessScale;

		let arcpath = '';
		const w = this.width;
		const h = this.height;
		this.hw = w * 0.5;
		this.hh = h * 0.5;

		svgEl.setAttributeNS(null, 'width', w+'');
		svgEl.setAttributeNS(null, 'height', h+'');
		svgEl.setAttributeNS(null, 'viewBox', '0 0 '+w+' '+ h);
		svgShadowEl.setAttributeNS(null, 'width', w+'');
		svgShadowEl.setAttributeNS(null, 'height', h+'');
		svgShadowEl.setAttributeNS(null, 'viewBox', '0 0 '+w+' '+ h);

		for (let i = 0; i < values.length; i++) {

			const shadowpath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
			shadowpath.setAttributeNS(null, 'fill', '#DDD');
			shadowpath.setAttributeNS(null, 'stroke', '#AAA');
			shadowpath.setAttributeNS(null, 'class', 'rnz-shadow-path ' +classes[i]);
			svgShadowEl.appendChild(shadowpath);

			const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
			path.setAttributeNS(null, 'fill', '#DDD');
			path.setAttributeNS(null, 'stroke', '#AAA');
			path.setAttributeNS(null, 'class', 'rnz-path ' +classes[i]);
			svgEl.appendChild(path);

			this.rings.push({
				value: values[i],
				class: classes[i],
				path: path,
				shadow: shadowpath,
			});

			//True radial segment without rounded corners:

			// const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
			// path2.setAttributeNS(null, 'fill', 'transparent');
			// path2.setAttributeNS(null, 'stroke', 'blue');
			// arcpath = '';
			//
			// arcpath = 'M '+(arcStartPos[0]*radMax+arcX) + ' ' + (arcStartPos[1]*radMax+arcY) + ' ';
			// arcpath += 'A '+ radMax + ' ' + radMax +' 0 0 1 ' +(arcEndPos[0]*radMax+arcX) + ' ' + (arcEndPos[1]*radMax+arcY) + ' ';
			// arcpath += 'L '+(arcEndPos[0]*radMin+arcX) + ' ' + (arcEndPos[1]*radMin+arcY) + ' ';
			// arcpath += 'A '+ radMin + ' ' + radMin +' 0 0 0 ' +(arcStartPos[0]*radMin+arcX) + ' ' + (arcStartPos[1]*radMin+arcY) + ' ';
			// arcpath += ' Z';
			// path2.setAttributeNS(null, 'd', arcpath);
			// svgEl.appendChild(path2);

			this.drawArc(i, values[i]);
		}
	}

	public drawArc(index: number, value: number)
	{
		const arcStart = -Math.PI * 0.5;
		const arcLength = Math.PI * value;
		const arcY = this.hh;
		const arcThickness = this.hh*0.085 * this.thicknessScale;
		const gapBetweenArcs = 0.3;
		const arcRadius = this.hh-arcThickness*(3+index*(2+gapBetweenArcs));
		const arcX = this.hw-this.hh*0.5;

		const cornerRadSize = arcThickness*0.5;
		//Rounded corner grows with the total length of the arc (radius*length) but also grows as the arc gets smaller to keep proportions
		let cornerArcSize = (arcRadius*arcLength) * 0.00007 + (Math.PI - arcLength)* 0.020;
		//Dont let rounded corner go larger than the size of the arc itself.
		cornerArcSize = -Math.max(arcLength*0.5-(cornerArcSize), 0) + arcLength*0.5;

		let arcpath = '';

		const arcStartPos = [Math.cos(arcStart), Math.sin(arcStart)];
		const arcStartCornerPos = [Math.cos(arcStart+cornerArcSize), Math.sin(arcStart+cornerArcSize)];
		const arcEndPos = [Math.cos(arcStart+arcLength), Math.sin(arcStart+arcLength)];
		const arcEndCornerPos = [Math.cos(arcStart+arcLength-cornerArcSize), Math.sin(arcStart+arcLength-cornerArcSize)];

		const radMax = arcRadius+arcThickness;
		const radMaxCorner = arcRadius+arcThickness-cornerRadSize;
		const radMin = arcRadius-arcThickness;
		const radMinCorner = arcRadius-arcThickness+cornerRadSize;

		//Move to start
		arcpath = 'M '+(arcStartPos[0]*radMaxCorner+arcX) + ' ' + (arcStartPos[1]*radMaxCorner+arcY) + ' ';
		//Top Left Rounded corner
		arcpath += 'Q '+(arcStartPos[0]*radMax+arcX) + ' ' + (arcStartPos[1]*radMax+arcY) + ', '+(arcStartCornerPos[0]*radMax+arcX) + ' ' + (arcStartCornerPos[1]*radMax+arcY) + ' ';
		//Arc to bottom
		arcpath += 'A '+ radMax + ' ' + radMax +' 0 0 1 ' +(arcEndCornerPos[0]*radMax+arcX) + ' ' + (arcEndCornerPos[1]*radMax+arcY) + ' ';
		//Top right rounded corner
		arcpath += 'Q '+(arcEndPos[0]*radMax+arcX) + ' ' + (arcEndPos[1]*radMax+arcY) + ', '+(arcEndPos[0]*radMaxCorner+arcX) + ' ' + (arcEndPos[1]*radMaxCorner+arcY) + ' ';

		//Close the bottom off with a line
		arcpath += 'L '+(arcEndPos[0]*radMinCorner+arcX) + ' ' + (arcEndPos[1]*radMinCorner+arcY) + ' ';
		//Bottom right rounded corner
		arcpath += 'Q '+(arcEndPos[0]*radMin+arcX) + ' ' + (arcEndPos[1]*radMin+arcY) + ', '+(arcEndCornerPos[0]*radMin+arcX) + ' ' + (arcEndCornerPos[1]*radMin+arcY) + ' ';
		//Arc back up to the top
		arcpath += 'A '+ radMin + ' ' + radMin +' 0 0 0 ' +(arcStartCornerPos[0]*radMin+arcX) + ' ' + (arcStartCornerPos[1]*radMin+arcY) + ' ';
		//Bottom left rounded corner
		arcpath += 'Q '+(arcStartPos[0]*radMin+arcX) + ' ' + (arcStartPos[1]*radMin+arcY) + ', '+(arcStartPos[0]*radMinCorner+arcX) + ' ' + (arcStartPos[1]*radMinCorner+arcY) + ' ';


		arcpath += ' Z';
		this.rings[index].path.setAttributeNS(null, 'd', arcpath);
		this.rings[index].shadow.setAttributeNS(null, 'd', arcpath);

	}
}

export default class RoadmapToNetZero extends Scene {

	sectionSelector = '#roadmap-to-net-zero';

	graph: RadialGraph[];

	constructor(manager: SceneManager, targetContainer: string) {
		super(manager, targetContainer);

		this.cloneRightGraphElements();

		this.graph = [
			new RadialGraph(getEl('#rnz-graph-1'),getEl('#rnz-graph-1-shadow'), [0, 0, 0], ['','','fill-blue']),
			new RadialGraph(getEl('#rnz-graph-2'),getEl('#rnz-graph-2-shadow'), [0, 0], ['','fill-blue'], 1.3),
		];
		
		//Read each percent value and convert to values of the graph from 0 to 1.
		const graph1Progress      = parseFloat((getEl('.rnz-graph-1-container .rnz-progress') as HTMLElement).dataset?.value ?? '0') / 50;
		const graph1YearTarget    = parseFloat((getEl('.rnz-graph-1-container .rnz-yeartarget') as HTMLElement).dataset?.value ?? '0') / 50;
		const graph1DecadeTarget  = parseFloat((getEl('.rnz-graph-1-container .rnz-decadetarget') as HTMLElement).dataset?.value ?? '0') / 50;
		
		const graph2Progress      = parseFloat((getEl('.rnz-graph-2-container .rnz-progress') as HTMLElement).dataset?.value ?? '0') / 50;
		const graph2DecadeTarget  = parseFloat((getEl('.rnz-graph-2-container .rnz-decadetarget') as HTMLElement).dataset?.value ?? '0') / 50;
		
		//These values specify the actual values of the graph from 0.0 -> no arc (0%) to 1.0 -> Full half arc (50%).
		//So the values 1 = 50%, 0.6 = 30%, 0.48 = 24%.
		this.createGraphAnimation(1, [graph1DecadeTarget, graph1YearTarget, graph1Progress]); //50% 30% 30%
		this.createGraphAnimation(2, [graph2DecadeTarget, graph2Progress]); //30% and 25%

	}


	public createGraphAnimation(graphNumber: number = 1, targetValues: number[])
	{
		const outerContainer = getEl('.rnz-graph-'+graphNumber+'-container');
		const innerContainer = getEl('.rnz-graph-'+graphNumber+'-container .rnz-svg-container');
		const svg = getEl('#rnz-graph-'+graphNumber+'');
		const svgShadow = getEl('#rnz-graph-'+graphNumber+'-shadow');

		//@ts-ignore
		outerContainer.value = 0;

		// this.addScrollAnimation({
		// 	targets: outerContainer,
		// 	duration: 1500,
		// 	delay: 0,
		// 	value: [0, 100],
		// 	easing: 'linear',
		// 	update: () => {
		// 		//Amount of degrees to tilt when scrolling
		// 		//@ts-ignore
		// 		const deg = ((100-outerContainer.value)*0.01 - 0.5)*1.5;
		//
		// 		//@ts-ignore
		// 		const val = outerContainer.value;
		// 		//@ts-ignore
		// 		innerContainer.style.perspectiveOrigin = '-20% '+(100-val)+'%';
		//
		// 		//@ts-ignore
		// 		svg.style.transform = 'rotateX('+(deg)+'deg)';
		// 		//@ts-ignore
		// 		svgShadow.style.transform = 'rotateX('+(deg)+'deg) translate3d(0,0,-2px)';
		//
		// 	},
		// }, outerContainer, 300, 0, 1, 0.1, -0.2, 0.5);

		let vals = [
			getEl('.rnz-graph-'+graphNumber+'-container .rnz-progress'),
			getEl('.rnz-graph-'+graphNumber+'-container .rnz-yeartarget'),
			getEl('.rnz-graph-'+graphNumber+'-container .rnz-decadetarget'),
		];
		let valElements = [
			getEls('.rnz-graph-'+graphNumber+'-container .rnz-progress'),
			getEls('.rnz-graph-'+graphNumber+'-container .rnz-yeartarget'),
			getEls('.rnz-graph-'+graphNumber+'-container .rnz-decadetarget'),
		];

		const isMobile = this.manager.app.responser.isSmallerOrEqualTo(ResponsiveSize.md);

		let graphVals:{val:number, target:number}[] = [];
		targetValues.forEach((item) => {
			graphVals.push({val: 0, target: item});
		})

		//Remove any elements which are not valid. Graph 2 doesnt have a .rnz-yeartarget.
		vals = vals.filter((el) => !!el);
		valElements = valElements.filter((elList) => elList.length != 0);

		vals.forEach((item) => {
				//@ts-ignore
				item.valuemax = parseInt(item.dataset.value ?? "0");
				//@ts-ignore
				item.val = 0;
				//@ts-ignore
				item.graphVal = 0;
		});

		const valuesTimeline = anime.timeline({
			easing: "easeInOutQuad",
			autoplay: false,
		}).add({
			targets: vals,
			val: [0,100],
			easing: "easeInOutQuart",
			duration: 2000,
			update: () => {
				for (let i= 0; i < vals.length; i++)
				{

					for (let j = 0; j < valElements[i].length; j++) {
						//@ts-ignore
						valElements[i][j].innerHTML = Math.round(vals[i].valuemax * (vals[i].val * 0.01)) + '';
					}
				}
			},
		});

		const graphsTimeline = anime.timeline({
			easing: "easeInOutQuad",
			autoplay: false,
		})
		.add({
			targets: innerContainer,
			opacity: [0,1],
			//filter: ['blur(20px)', 'blur(0px)'],
			easing: "easeInQuad",
			duration: 100,
		})
		.add({
			targets: graphVals,
			val: [0,100],
			easing: "easeInOutQuart",
			duration: 2000,
			update: () => {
				for (let i = 0; i < graphVals.length; i++) {
					const t = (graphVals[i].val * 0.01);
					this.graph[graphNumber-1].drawArc(i, t * graphVals[i].target);
				}
			},
		});

		this.manager.app.scroller.addVisibilityTrigger(outerContainer, (dir) => {
			if (!graphsTimeline.completed) graphsTimeline.play();
			if (!valuesTimeline.completed) valuesTimeline.play();
		}, isMobile ? 0.55 : 0.5);
	}

	onResponsiveChange(newSize: ResponsiveSize, oldSize: ResponsiveSize, responser: ResponsiveWatcher) {
		// if (isSmallerThanOrEqualTo(newSize, ResponsiveSize.md) && isGreaterThanOrEqualTo(oldSize, ResponsiveSize.lg))
		// {
		// 	//We are in mobile and came from desktop.
		//
		// }
		// else if (isGreaterThanOrEqualTo(newSize, ResponsiveSize.lg) && isSmallerThanOrEqualTo(oldSize, ResponsiveSize.md))
		// {
		// 	//We are in desktop and we come from mobile.
		//
		// }
	}

	cloneRightGraphElements()
	{
		const graphs = getEls('.rnz-graph-container');
		graphs.forEach((graph) => {
			const desktopEl = graph.querySelector('.rnz-graph-right');
			const mobileEl = graph.querySelector('.rnz-graph-right-mobile');

			if (desktopEl && mobileEl)
			{
					if (!graph.classList.contains('rnz-cloned')) {
						mobileEl.innerHTML = desktopEl.innerHTML;
						graph.classList.add('rnz-swapped');
					}
			}
		});
		//clearElCache();
	}

	onEnteredView(dir: ScrollDirection) {
		super.onEnteredView(dir);
		setDownloadReportURLPage(9);
	}

	onExitedView(dir: ScrollDirection) {
		super.onExitedView(dir);
		setDownloadReportURLPage(false);
	}



}
